import React, { useReducer, createContext, useEffect } from 'react'
import { jwtDecode } from 'jwt-decode'
import { gql } from '@apollo/client'
import client, { refreshWebSocketConnection } from '../utils/apollo-client'

const LOGOUT_MUTATION = gql`
    mutation Logout {
        logout {
            success
            message
        }
    }
`

const GET_USER_ONBOARDING = gql`
    query GetUserOnboarding($userId: ID!) {
        users(where: { id: $userId }) {
            id
            onboarding
        }
    }
`

const initialState = {
    user: null,
    onboarding: false,
}

if (localStorage.getItem('token')) {
    // we use the token in storage and allow the server
    // to determine if it can replace any expired access tokens or reject access
    initialState.user = jwtDecode(localStorage.getItem('token'))
}

const AuthContext = createContext({
    user: null,
    onboarding: null,
    login: (userData) => {},
    logout: () => {},
    setOnboarding: (value) => {},
})

const authReducer = (state, action) => {
    switch (action.type) {
        case 'LOGIN':
            return {
                ...state,
                user: jwtDecode(action.payload.token),
            }
        case 'LOGOUT':
            return {
                ...state,
                user: null,
                onboarding: null,
            }
        case 'UPDATE_TOKEN':
            return {
                ...state,
                user: jwtDecode(action.payload.token),
            }
        case 'SET_ONBOARDING':
            return {
                ...state,
                onboarding: action.payload,
            }
        default:
            return state
    }
}

const AuthProvider = (props) => {
    const [state, dispatch] = useReducer(authReducer, initialState)

    const setOnboarding = (value) => {
        dispatch({
            type: 'SET_ONBOARDING',
            payload: value,
        })
    }

    const fetchOnboarding = async (userId) => {
        const { data } = await client.query({
            query: GET_USER_ONBOARDING,
            variables: { userId },
            fetchPolicy: 'network-only',
        })
        if (data?.users?.[0]) {
            setOnboarding(data.users[0].onboarding)
        }
    }

    // Fetch onboarding status whenever user exists
    useEffect(() => {
        if (state.user?.id) fetchOnboarding(state.user.id)
    }, [state.user?.id])

    const login = async (userData) => {
        localStorage.setItem('token', userData.token)
        dispatch({
            type: 'LOGIN',
            payload: userData,
        })
    }

    const updateToken = (newToken) => {
        localStorage.setItem('token', newToken)
        dispatch({
            type: 'UPDATE_TOKEN',
            payload: newToken,
        })
    }

    const logout = () => {
        // Immediate UI operations - no blocking
        localStorage.removeItem('token')
        dispatch({ type: 'LOGOUT' })

        // Immediate WebSocket refresh - no blocking
        refreshWebSocketConnection()

        // Fire-and-forget server cleanup - non-blocking
        client
            .mutate({
                mutation: LOGOUT_MUTATION,
            })
            .catch((error) => {
                console.error('Server logout error (non-blocking):', error)
            })
    }

    return (
        <AuthContext.Provider
            value={{ user: state.user, onboarding: state.onboarding, login, logout, updateToken, setOnboarding }}
            {...props}
        />
    )
}

export { AuthContext, AuthProvider }
